import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Home, Sun, BatteryCharging, Banknote, TrendingUp,
  Wrench, ShieldCheck, ArrowRight, PhoneCall, CreditCard, Zap
} from "lucide-react";

const INSTALL_IMG_1 = "https://images.unsplash.com/photo-1660330589505-9a433a742a7b?auto=format&fit=crop&q=80&w=800";
const INSTALL_IMG_2 = "https://images.unsplash.com/photo-1624397640148-949b1732bb0a?auto=format&fit=crop&q=80&w=800";
const HOUSE_IMG = "https://images.unsplash.com/photo-1770567764570-ebe9b5d0c02b?auto=format&fit=crop&q=80&w=800";

const installSteps = [
  {
    icon: PhoneCall,
    number: "01",
    title: "Fale com a gente",
    description: "Entre em contato pelo WhatsApp ou formulário. Vamos entender seu consumo e necessidades.",
  },
  {
    icon: Wrench,
    number: "02",
    title: "Projeto e instalação",
    description: "Nossa equipe técnica projeta o sistema ideal e instala os painéis no seu telhado.",
  },
  {
    icon: Sun,
    number: "03",
    title: "Gere sua energia",
    description: "Os painéis começam a gerar energia solar imediatamente. Sua conta de luz vai a praticamente zero.",
  },
  {
    icon: TrendingUp,
    number: "04",
    title: "Lucre com o excedente",
    description: "Gerou mais do que consome? Venda a energia que sobrar e transforme seu telhado em fonte de renda.",
  },
];

const benefits = [
  {
    icon: CreditCard,
    title: "Parcelas no valor da sua conta",
    description: "O financiamento cabe no seu bolso: as parcelas ficam no valor que você já paga de conta de luz. Só que ao invés de pagar para a concessionária, você paga pelo que é SEU.",
    highlight: true,
  },
  {
    icon: Banknote,
    title: "Conta de luz zerada",
    description: "Com os painéis instalados, sua conta de luz é completamente abatida. Você só paga a taxa mínima da concessionária.",
    highlight: false,
  },
  {
    icon: TrendingUp,
    title: "Venda a energia excedente",
    description: "Se seus painéis gerarem mais energia do que você consome, você pode vender o excedente e ganhar dinheiro com isso.",
    highlight: false,
  },
  {
    icon: BatteryCharging,
    title: "Valorização do imóvel",
    description: "Imóveis com painéis solares valorizam de 3% a 6%. Além de economizar, seu patrimônio cresce.",
    highlight: false,
  },
  {
    icon: ShieldCheck,
    title: "Garantia de 25 anos",
    description: "Os painéis possuem garantia de longa duração. São décadas gerando energia limpa e economia pra você.",
    highlight: false,
  },
  {
    icon: Zap,
    title: "Independência energética",
    description: "Não fique refém dos aumentos nas tarifas de energia. Gere a sua própria energia e tenha previsibilidade.",
    highlight: false,
  },
];

export const InstallationSection = () => {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div id="instalacao" data-testid="installation-section">
      {/* Section Hero */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col-reverse md:flex-row items-center gap-10 md:gap-16">
            {/* Text */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1"
            >
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#f5b731]/15 text-[#e0a31f] mb-4">
                <Home className="w-3 h-3" />
                Painéis Solares
              </span>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4 leading-tight">
                Instale painéis solares e <span style={{ color: "#1a4fa0" }}>elimine sua conta de luz</span>
              </h2>
              <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-6 max-w-xl">
                Tenha seus próprios painéis solares no telhado. As parcelas do financiamento ficam no mesmo valor 
                que você já paga de conta de luz — só que ao final, o sistema é todo seu.
              </p>

              {/* Key selling points */}
              <div className="bg-[#f5b731]/8 border border-[#f5b731]/20 rounded-2xl p-5 mb-6">
                <p className="text-sm font-semibold text-slate-800 mb-2">O melhor de tudo:</p>
                <ul className="space-y-2">
                  {[
                    "Parcelas no valor da sua conta de luz atual",
                    "Conta de luz completamente abatida",
                    "Possibilidade de vender a energia que sobrar",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-slate-600">
                      <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: "rgba(34, 197, 94, 0.15)" }}>
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                data-testid="installation-cta"
                onClick={() => scrollTo("#contato")}
                className="rounded-full bg-[#1a4fa0] hover:bg-[#153e80] text-white h-12 px-8 text-base font-semibold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-transform duration-200"
              >
                Solicitar orçamento
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>

            {/* Images collage */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex-1 relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <img
                  src={INSTALL_IMG_1}
                  alt="Instalação de painéis solares"
                  className="rounded-2xl shadow-lg w-full h-48 md:h-64 object-cover"
                />
                <img
                  src={HOUSE_IMG}
                  alt="Casa com painéis solares"
                  className="rounded-2xl shadow-lg w-full h-48 md:h-64 object-cover mt-8"
                />
                <img
                  src={INSTALL_IMG_2}
                  alt="Técnico instalando painel"
                  className="rounded-2xl shadow-lg w-full h-48 md:h-64 object-cover col-span-2"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works - Installation */}
      <section id="como-funciona-instalacao" data-testid="how-it-works-installation" className="py-20 md:py-24" style={{ background: "#f8fafc" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#f5b731]/15 text-[#e0a31f] mb-4">
              Passo a Passo
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Como funciona a <span style={{ color: "#1a4fa0" }}>instalação?</span>
            </h2>
            <p className="text-base md:text-lg text-slate-500 max-w-xl mx-auto">
              Do contato ao primeiro kilowatt gerado, acompanhamos tudo:
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {installSteps.map((step, i) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 }}
                className="relative"
              >
                <div className="bg-white rounded-2xl p-7 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold text-sm mb-5" style={{ background: "#e0a31f" }}>
                    {step.number}
                  </div>
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-4" style={{ background: "rgba(26, 79, 160, 0.08)" }}>
                    <step.icon className="w-6 h-6" style={{ color: "#1a4fa0" }} />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.description}</p>
                </div>
                {i < installSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                    <div className="w-6 h-[2px]" style={{ background: "#e0a31f", opacity: 0.3 }} />
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits of Installation */}
      <section data-testid="installation-benefits" className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-[#1a4fa0]/10 text-[#1a4fa0] mb-4">
              Vantagens
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Por que instalar <span style={{ color: "#1a4fa0" }}>painéis solares?</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, i) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className={`group rounded-2xl p-7 border transition-shadow duration-300 hover:shadow-lg ${
                  benefit.highlight
                    ? "bg-[#1a4fa0] border-[#1a4fa0] shadow-lg shadow-[#1a4fa0]/20"
                    : "bg-white border-slate-100 shadow-sm"
                }`}
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5"
                  style={{
                    background: benefit.highlight ? "rgba(245, 183, 49, 0.2)" : "rgba(245, 183, 49, 0.1)",
                  }}
                >
                  <benefit.icon className="w-6 h-6" style={{ color: benefit.highlight ? "#f5b731" : "#e0a31f" }} />
                </div>
                <h3 className={`text-base font-bold mb-2 ${benefit.highlight ? "text-white" : "text-slate-900"}`}>
                  {benefit.title}
                </h3>
                <p className={`text-sm leading-relaxed ${benefit.highlight ? "text-white/80" : "text-slate-500"}`}>
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
